import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { translateDoc } from '../../app/mapWrites';
import { apiRequest } from '../../api/client';
import { buildHiddenSets, isPermHidden, canEditAllDocs } from '../../lib/permissions';
import { canTranslateDocsHere } from '../../lib/capabilities';
import {
  DOC_BEAN_COLOR,
  PIPELINE_COLOR,
  PIPELINE_DISPLAY,
  PIPELINE_LABEL,
  pipelineStageOf,
} from '../../lib/docs';
import { isExemptPlan } from '../../lib/health';
import { orderedLanguages } from '../../lib/docTable';
import { helpText } from '../../i18n/help';
import { NodeChip } from '../../components/NodeChip';
import { DocPill } from '../../components/DocPill';
import type { MapNode, NodeId, PipelineStage } from '../../lib/types';

/**
 * The AI documentation console — the dashboard's `ai-docs` TAB since V0.44.1 (it was its own
 * full-screen modal, `#aidocmodal`, from the port to V0.44). Seven stage strips by PIPELINE
 * STAGE, select nodes, generate.
 *
 * WHY A TAB. The Documents tab lists every page with its workflow standing and asks "who writes
 * this, is it stale, what did the AI do"; this console is where the AI is told what to write.
 * Two full-screen surfaces for one backlog meant closing one to open the other and losing the
 * row you were looking at both times. As tabs of one region they hand a NODE to each other: a
 * row's "Configure" lands here on that node's stage strip with the list narrowed to it, and a
 * row's "See WF state" lands on the Documents tab narrowed to that node's pages.
 *
 * WHAT THE MOVE CHANGED, AND WHAT IT DID NOT. Gone: the host, the handle, the `open` state, the
 * `perm-head` (the dashboard's header owns the title, the language picker and the presence
 * stack), the `reportAdminScreen('ai-docs')` effect (the dashboard reports its tab), and the
 * `#aidoc-close` button (`#dashboard-close` is the way out). Mounting IS the reset the old
 * `open()` performed — the dashboard mounts only the selected tab. Kept, verbatim: every id and
 * class the regression suite drives (`#aidoc-tabs .perm-tab`, `#aidoc-body`, `#aidoc-search`,
 * `#aidoc-searchcount`, `#aidoc-selall`, `#aidoc-generate`, `#aidoc-progress`, `#aidoc-cancel`,
 * `.aidoc-actbtn`, `.aidoc-bulkbar`, `.rowstate`, `tr[data-node]`), the state, the actions, the
 * regeneration-guard answer and the poll.
 *
 * ONE CLEANUP FOR EVERY WAY OUT. The console used to flush edited prompts (`afterAidocClose`) from
 * its own close button and nowhere else, and stop its poll from a handle. Here both run in the
 * unmount cleanup, so a tab switch, `#dashboard-close`, Escape and the router's close all land on
 * the same line — the defect the old header described ("a generation still running when the
 * screen went away by any other route polled for ever") cannot come back by adding a fourth exit.
 *
 * TWO CROSS-LINKS DIFFER ON PURPOSE. "AI context" (`#aidoc-to-config`) opens over the dashboard:
 * that screen has no URL segment and its z-index is above ours. "Targets" (`#aidoc-to-targets`)
 * still closes the dashboard first: Targets IS an admin screen, and there is ONE `S.adminScreen`
 * slot — opened over us, its close would null the slot while the dashboard stayed visibly open,
 * and the URL and the presence roster would both say "map". The peer screens swap the same way.
 *
 * The footer's id is `#dash-ai-foot`, not the old `#aidoc-foot`: the AI context screen renders
 * an `#aidoc-foot` of its own and can now be open above this tab.
 *
 * V0.38 RESHAPED THIS SCREEN DELIBERATELY. The strips are the seven derived stages
 * (`pipelineStageOf` over the map's prompt-scoped `pipelineStage`), the old read-only Review
 * column DISSOLVED into them — the stage IS the row's state — and every per-row and bulk
 * STATE-TRANSITION button was REMOVED: transitions happen only where the text being judged is
 * readable, the doc bar's WorkflowControl. The lost "bulk approve" has no honest replacement,
 * and that is the point. What stays is the operator work: prompts, generation (answering the
 * regeneration guard's `skipped` list), translation, and the No-doc plan. The prompt boxes write
 * through an action, the search box is a controlled input, the poll is a ref with a cleanup.
 */

/** A console action: a generation, or a side effect. NEVER a state transition (V0.38). */
interface Action {
  label: string;
  cls: string;
  gen?: boolean;
  act?: 'nodoc' | 'translate';
}

/**
 * What can be done from each stage — generation and side effects only, since V0.38: the
 * per-row transition buttons went with the two-status split (see the header).
 *
 * Generation stays exactly where it lived before the rename: `prompt_ready` is the "ready to
 * run" strip (the old prompt_present) so Generate belongs on the row rather than only on the
 * footer button; `draft` holds the sent-back pages (the old doc_rejected) whose prompt you
 * edit and re-run from; `needs_update` is the flagged backlog. Translate is offered wherever
 * a page exists; the two pre-document stages offer the No-doc plan instead.
 */
function actionsForRaw(stage: PipelineStage): Action[] {
  switch (stage) {
    case 'no_prompt':
      return [{ label: 'No doc', act: 'nodoc', cls: 'nodoc' }];
    case 'prompt_ready':
      return [
        { label: 'Generate', gen: true, cls: 'regen' },
        { label: 'No doc', act: 'nodoc', cls: 'nodoc' },
      ];
    case 'draft':
      return [
        { label: 'Regenerate', gen: true, cls: 'regen' },
        { label: 'Translate', act: 'translate', cls: 'regen' },
      ];
    case 'generated':
      return [{ label: 'Translate', act: 'translate', cls: 'regen' }];
    case 'in_review':
      return [{ label: 'Translate', act: 'translate', cls: 'regen' }];
    case 'approved':
      return [{ label: 'Translate', act: 'translate', cls: 'regen' }];
    case 'needs_update':
      return [
        { label: 'Regenerate', gen: true, cls: 'regen' },
        { label: 'Translate', act: 'translate', cls: 'regen' },
      ];
    default:
      return [];
  }
}

interface PerLocale {
  locale: string;
  version: number | null;
  stale?: boolean;
  behind?: number;
}
interface LocaleSummary {
  sourceLocale: string;
  languages: { code: string; name?: string }[];
  byDoc: Record<string, PerLocale[]>;
}

type RowState = { cls: string; text: string };

/** Which locales exist for one page, and which have fallen behind the source — the source
 *  first, then the rest by code, the order every language strip in the dashboard uses (V0.44.1). */
function LangCell({ node, locales }: { node: MapNode; locales: LocaleSummary | null }) {
  if (!locales || !node.docId) {
    return <span style={{ color: 'var(--ink-faint)', fontSize: 12 }}>—</span>;
  }
  const byCode = new Map((locales.byDoc[node.docId] || []).map((p) => [p.locale, p]));
  const langs: PerLocale[] = orderedLanguages(locales.languages, locales.sourceLocale).map(
    (l) =>
      byCode.get(l.code) ?? {
        locale: l.code,
        version: null,
        stale: l.code !== locales.sourceLocale,
        behind: 0,
      },
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {langs.map((l) => {
        const isSource = l.locale === locales.sourceLocale;
        const behind = !isSource && l.stale;
        return (
          <span
            key={l.locale}
            className={l.version ? 'ld-flag on' : 'ld-flag'}
            title={
              isSource
                ? 'Source language — written, not translated'
                : behind
                  ? l.version
                    ? `${l.behind} version(s) behind ${locales.sourceLocale.toUpperCase()}`
                    : 'Not translated yet'
                  : undefined
            }
            style={
              behind
                ? {
                    borderColor: 'var(--status-needs-update,#e0762d)',
                    color: 'var(--status-needs-update,#e0762d)',
                  }
                : undefined
            }
          >
            {l.locale.toUpperCase()}
            {l.version ? ` V${l.version}` : ' —'}
          </span>
        );
      })}
    </div>
  );
}

export function AiTab({
  focusNodeId,
  onLeave,
  onShowInDocuments,
}: {
  /** A node handed over by the Documents tab's "Configure": land on its stage, narrowed to it. */
  focusNodeId: NodeId | null;
  /** Close the dashboard — Cancel, and the Targets cross-link (see the header). */
  onLeave: () => void;
  /** "See WF state": hand this node to the Documents tab. */
  onShowInDocuments: (nodeId: NodeId) => void;
}) {
  const s = useAppState();
  // The hand-off is read ONCE, as initial state: the dashboard mounts this tab fresh per select.
  const [focusNode, setFocusNode] = useState<NodeId | null>(focusNodeId);
  const [tab, setTab] = useState<PipelineStage>(
    () => (focusNodeId ? pipelineStageOf(s.nodes[focusNodeId]) : undefined) ?? 'no_prompt',
  );
  const [selected, setSelected] = useState<ReadonlySet<NodeId>>(new Set());
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [locales, setLocales] = useState<LocaleSummary | null>(null);
  const [rowState, setRowState] = useState<Record<NodeId, RowState>>({});
  const [progress, setProgress] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localesRef = useRef<LocaleSummary | null>(null);
  localesRef.current = locales;

  const stopPolling = useCallback(() => {
    if (poll.current) clearInterval(poll.current);
    poll.current = null;
  }, []);

  // EVERY way out of this tab runs through here (see the header): stop a poll that would
  // otherwise outlive the screen, and persist the prompts edited in the boxes.
  useEffect(
    () => () => {
      stopPolling();
      callAction('afterAidocClose');
    },
    [stopPolling],
  );

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => setQuery(search), 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [search]);

  const loadLocales = useCallback(async () => {
    try {
      const d = await apiRequest<LocaleSummary>('/api/docs/revisions/locale-summary');
      setLocales(d);
      localesRef.current = d;
      return d;
    } catch {
      return null; // the language column simply does not render
    }
  }, []);

  // One summary call for the whole console rather than one per row.
  useEffect(() => {
    void loadLocales();
  }, [loadLocales, s.currentLocale]);

  const hidden = useMemo(() => buildHiddenSets(s.perm, s.tagEdges), [s.perm, s.tagEdges]);

  /** Every node at a pipeline stage, minus roots, hidden nodes and EXEMPT ones (plan = {}). A node
   *  whose stage this viewer may not see (`pipelineStageOf` → undefined) is in no strip — for
   *  the AI operator this console is gated to, the payload carries every node's stage. */
  const nodesFor = useCallback(
    (stage: PipelineStage): MapNode[] =>
      Object.keys(s.nodes)
        .filter((id) => {
          const n = s.nodes[id];
          if (n.root || isPermHidden(id, s.edges, hidden)) return false;
          if (isExemptPlan(n.expectedDocs)) return false;
          return pipelineStageOf(n) === stage;
        })
        .map((id) => s.nodes[id]),
    [s.nodes, s.edges, s.tags, hidden],
  );

  // Translation is filtered out where the deployment has no translator, so the action never
  // appears rather than appearing and returning 503.
  const canTranslate = canTranslateDocsHere(s.perm, s.caps);
  // The No-doc action WRITES a documentation plan (expectedDocs = {}), gated server-side by
  // mayAssignNode — which does NOT admit a plain AI_ADMIN. Withhold it here for an operator
  // without EDIT_ALL_DOCS, or every click is a 403 (V0.42).
  const canExempt = canEditAllDocs(s.perm);
  const actions = useMemo(() => {
    let raw = actionsForRaw(tab);
    if (!canTranslate) raw = raw.filter((a) => a.act !== 'translate');
    if (!canExempt) raw = raw.filter((a) => a.act !== 'nodoc');
    return raw;
  }, [tab, canTranslate, canExempt]);

  const full = nodesFor(tab);
  const q = query.trim().toLowerCase();
  // The hand-off narrows the strip to ONE node; otherwise the search spans the label and the
  // prompt — the two things you hunt by when a strip holds a hundred nodes.
  const list = focusNode
    ? full.filter((n) => n.id === focusNode)
    : q
      ? full.filter(
          (n) =>
            (n.label || '').toLowerCase().includes(q) || (n.aiPrompt || '').toLowerCase().includes(q),
        )
      : full;

  const translate = useCallback(
    async (ids: NodeId[]) => {
      const loc = localesRef.current;
      if (!loc) {
        setProgress('Language list not loaded yet — reopen this tab.');
        return;
      }
      const targets = loc.languages.map((l) => l.code).filter((c) => c !== loc.sourceLocale);
      if (!targets.length) {
        setProgress('No other languages are configured.');
        return;
      }
      const docs = ids.map((id) => s.nodes[id]).filter((n) => n && n.docId);
      if (!docs.length) {
        setProgress('None of the selected nodes has a documentation page yet.');
        return;
      }
      if (
        !confirm(
          `Translate ${docs.length} page${docs.length === 1 ? '' : 's'} into ` +
            `${targets.join(', ').toUpperCase()}?\n\n` +
            `${loc.sourceLocale.toUpperCase()} stays the master copy — translations are made from it.`,
        )
      ) {
        return;
      }

      let done = 0;
      let failed = 0;
      // Sequential, like generation, to stay gentle on the API.
      for (const n of docs) {
        for (const target of targets) {
          setProgress(
            `Translating ${n.label} → ${target.toUpperCase()} (${done + failed}/${docs.length * targets.length})…`,
          );
          try {
            await translateDoc(n.docId!, target);
            done++;
          } catch (e) {
            failed++;
            setProgress(
              `${n.label} → ${target.toUpperCase()}: ${e instanceof Error ? e.message : 'failed'}`,
            );
          }
        }
      }
      setProgress(
        `Translated ${done} page${done === 1 ? '' : 's'}${failed ? `, ${failed} failed` : ''}.`,
      );
      await loadLocales();
    },
    [s.nodes, loadLocales],
  );

  const pollJob = useCallback(
    async (jobId: string) => {
      let data: {
        finished?: boolean;
        nodes?: Record<NodeId, { state: string; docId?: string; title?: string; error?: string }>;
      };
      try {
        data = await apiRequest(`/api/ai/generate-docs/${encodeURIComponent(jobId)}`);
      } catch {
        return; // a dropped poll is not a failed job; the next tick asks again
      }

      const st = data.nodes || {};
      const next: Record<NodeId, RowState> = {};
      let done = 0;
      let total = 0;
      for (const id in st) {
        total++;
        const node = st[id];
        if (node.state === 'done') {
          done++;
          next[id] = { cls: 'done', text: 'Generated' };
          // The map's copy of the node has to learn it has a page now. That is map state, so
          // it goes back through the side that owns it.
          callAction('applyGeneratedDoc', id, node.docId ?? null, node.title ?? null);
        } else if (node.state === 'error') {
          done++;
          next[id] = { cls: 'error', text: node.error ? `Error: ${node.error}` : 'Error' };
        } else if (node.state === 'generating') {
          next[id] = { cls: 'generating', text: 'Generating…' };
        } else {
          next[id] = { cls: 'pending', text: 'Queued' };
        }
      }
      setRowState((prev) => ({ ...prev, ...next }));
      setProgress(`Progress: ${done} / ${total}`);

      if (!data.finished) return;

      stopPolling();
      setGenerating(false);
      const loc = await loadLocales();

      // The source language has just changed, so every other language is now behind. Offer
      // the follow-up here rather than making it a separate hunt through the console.
      const written = Object.keys(st).filter((id) => st[id].state === 'done');
      const others = loc ? loc.languages.filter((l) => l.code !== loc.sourceLocale) : [];
      if (written.length && others.length && canTranslate) {
        const names = others.map((l) => l.code.toUpperCase()).join(', ');
        if (
          confirm(
            `${written.length} page${written.length === 1 ? '' : 's'} written in ` +
              `${loc!.sourceLocale.toUpperCase()}.\n\nTranslate now into ${names}?`,
          )
        ) {
          void translate(written);
        }
      }
    },
    [stopPolling, loadLocales, canTranslate, translate],
  );

  const generate = useCallback(
    async (ids: NodeId[]) => {
      if (!ids.length) {
        setProgress('Select at least one node.');
        return;
      }
      setGenerating(true);
      setProgress('Starting…');
      await callAction('flushAiFields'); // persist edited prompts before generating

      // A bulk run SKIPS nodes the regeneration guard protects (V0.38) — human edits since
      // the last generation — and says which. One protected page must not veto a whole run;
      // overriding is the single-node flow's confirm, deliberately not offered in bulk.
      let jobId: string;
      let skipped: { nodeId: NodeId; count: number }[] = [];
      try {
        const r = await apiRequest<{ jobId: string; skipped?: { nodeId: NodeId; count: number }[] }>(
          '/api/ai/generate-docs',
          {
            method: 'POST',
            body: { nodeIds: ids, lang: s.currentLocale },
          },
        );
        jobId = r.jobId;
        skipped = r.skipped ?? [];
      } catch (e) {
        setProgress(`Failed to start: ${e instanceof Error ? e.message : 'unknown error'}`);
        setGenerating(false);
        return;
      }

      const skippedIds = new Set(skipped.map((x) => x.nodeId));
      setRowState((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          next[id] = skippedIds.has(id)
            ? { cls: 'error', text: 'Skipped — human edits' }
            : { cls: 'pending', text: 'Queued' };
        }
        return next;
      });
      if (skipped.length) {
        setProgress(
          `Started — skipped ${skipped.length} node${skipped.length === 1 ? '' : 's'} with human edits since the last generation.`,
        );
      }

      stopPolling();
      const tick = () => void pollJob(jobId);
      poll.current = setInterval(tick, 1500);
      tick();
    },
    [s.currentLocale, stopPolling, pollJob],
  );

  const allSelected = list.length > 0 && list.every((n) => selected.has(n.id));

  const run = (a: Action, ids: NodeId[]) => {
    if (!ids.length) return;
    if (a.gen) void generate(ids);
    else if (a.act === 'translate') void translate(ids);
    else if (a.act === 'nodoc') void callAction('applyAidocExempt', ids);
    // No further arm: state transitions left this screen with V0.38 (see the header).
  };

  const bulk = (a: Action) => {
    const ids = list.filter((n) => selected.has(n.id)).map((n) => n.id);
    if (!ids.length) {
      setProgress('Select at least one node in this strip.');
      return;
    }
    run(a, ids);
  };

  const toggle = (id: NodeId, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const ActionButtons = ({ ids, onRun }: { ids?: NodeId[]; onRun?: (a: Action) => void }) => (
    <div className="aidoc-actions">
      {actions.map((a) => (
        <button
          key={a.label}
          className={`aidoc-actbtn ${a.cls}`}
          onClick={() => (onRun ? onRun(a) : run(a, ids || []))}
        >
          {a.label}
        </button>
      ))}
    </div>
  );

  const focusLabel = focusNode ? s.nodes[focusNode]?.label || 'one node' : '';

  return (
    <div id="dash-ai-tab">
      <div className="dt-tab-lead">
        <div className="perm-section-lead">
          Tell the AI what to write, node by node: a prompt per node, a strip per pipeline stage,
          then generate or translate the ones you select. Clearing a page still happens on the page
          itself.
        </div>
        <div className="dt-tab-actions">
          <button
            className={showHelp ? 'btn on' : 'btn'}
            id="aidoc-info"
            onClick={() => setShowHelp((h) => !h)}
          >
            <svg className="ico">
              <use href="#i-info" />
            </svg>
            <span>Information</span>
          </button>
          {/* Opens OVER the dashboard: the AI context screen has no URL segment of its own. */}
          <button className="btn" id="aidoc-to-config" onClick={() => callAction('openAiDocConfig')}>
            <svg className="ico">
              <use href="#i-ai" />
            </svg>
            <span>AI context</span>
          </button>
          {/* Closes the dashboard first — Targets is an admin screen with its own URL, and there
              is one admin-screen slot (see the header). */}
          <button
            className="btn"
            id="aidoc-to-targets"
            onClick={() => {
              onLeave();
              callAction('openTargets');
            }}
          >
            <svg className="ico">
              <use href="#i-server" />
            </svg>
            <span>Targets</span>
          </button>
        </div>
      </div>

      {showHelp ? (
        <div
          id="aidoc-help"
          className="cfg-help"
          dangerouslySetInnerHTML={{ __html: helpText('generate', s.currentLocale) }}
        />
      ) : null}

      {/* Each strip carries its own count, so the shape of the backlog is visible without
          opening them one at a time. The strips ARE the pipeline (V0.38): seven derived
          stages, each hinting what standing there means. */}
      <div className="perm-tabs" id="aidoc-tabs">
        {PIPELINE_DISPLAY.map((st) => (
          <button
            key={st.key}
            className={st.key === tab ? 'perm-tab on' : 'perm-tab'}
            title={st.hint}
            onClick={() => setTab(st.key)}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flex: '0 0 auto',
                background: PIPELINE_COLOR[st.key],
              }}
            />
            {PIPELINE_LABEL[st.key]} <span style={{ opacity: 0.7 }}>({nodesFor(st.key).length})</span>
          </button>
        ))}
      </div>

      <div id="aidoc-body">
        <div>
          <div className="logs-controls">
            <input
              className="fld"
              id="aidoc-search"
              placeholder="Search this strip by node name or prompt…"
              style={{ flex: 1, minWidth: 220 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {focusNode ? (
              <button
                type="button"
                className="dt-strip-chip on"
                id="aidoc-focus-clear"
                title="Handed over from the Documents tab — show every node in this strip again"
                onClick={() => setFocusNode(null)}
              >
                Showing {focusLabel} · Show all
              </button>
            ) : null}
            <span className="tgt-meta" id="aidoc-searchcount">
              {q || focusNode
                ? `${list.length} of ${full.length}`
                : `${full.length} node${full.length === 1 ? '' : 's'}`}
            </span>
          </div>

          {!list.length ? (
            <div className="aidoc-empty">
              {focusNode
                ? `“${focusLabel}” is not in “${PIPELINE_LABEL[tab]}”.`
                : q
                  ? `No nodes in “${PIPELINE_LABEL[tab]}” match “${search}”.`
                  : `No nodes in “${PIPELINE_LABEL[tab]}”.`}
            </div>
          ) : (
            <>
              {actions.length ? (
                <div className="aidoc-bulkbar">
                  <span className="aidoc-bulklabel">Selected in this strip →</span>
                  <ActionButtons onRun={bulk} />
                </div>
              ) : null}

              <div className="card">
                <table className="utable" id="dash-ai">
                  {/* Fixed layout (#dash-ai in app.css) reads this colgroup: every column but the
                      prompt has a set width — the Documents tab's widths where the columns mean
                      the same thing — so the prompt column absorbs the rest of the card. */}
                  <colgroup>
                    <col style={{ width: 34 }} />
                    <col style={{ width: 230 }} />
                    <col />
                    <col style={{ width: 250 }} />
                    <col style={{ width: 150 }} />
                    {actions.length ? <col style={{ width: 210 }} /> : null}
                    <col style={{ width: 96 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          id="aidoc-selall"
                          title="Select all in this strip"
                          checked={allSelected}
                          onChange={(e) =>
                            setSelected((prev) => {
                              const next = new Set(prev);
                              for (const n of list) {
                                if (e.target.checked) next.add(n.id);
                                else next.delete(n.id);
                              }
                              return next;
                            })
                          }
                        />
                      </th>
                      <th>Node</th>
                      <th>AI prompt</th>
                      <th>Documentation</th>
                      <th>Languages</th>
                      {actions.length ? <th>Action</th> : null}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((n) => {
                      const state = rowState[n.id];
                      const title = n.docId ? s.docTitles[n.docId] || 'Open doc' : '';
                      return (
                        <tr key={n.id} data-node={n.id} className={n.id === focusNode ? 'flash' : undefined}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selected.has(n.id)}
                              onChange={(e) => toggle(n.id, e.target.checked)}
                            />
                          </td>
                          <td>
                            <NodeChip node={n} isLight={s.isLight} />
                          </td>
                          <td>
                            {/* Controlled, and the write goes through an action. The vanilla
                                version assigned straight into the node object from an input
                                event, which changed the map with nothing re-rendering. */}
                            <textarea
                              className="aidoc-prompt"
                              placeholder="Instructions for the AI…"
                              value={n.aiPrompt || ''}
                              onChange={(e) => callAction('setAiPrompt', n.id, e.target.value)}
                            />
                          </td>
                          <td>
                            <div className="dt-aidoc-cell">
                              {n.docId ? (
                                <>
                                  {/* The app's own pill (components/DocPill.tsx), the primary
                                      slot's colour. Opens over the dashboard, which stays where
                                      it is beneath the document. */}
                                  <DocPill
                                    variant="nav"
                                    label={title}
                                    title={title}
                                    color={DOC_BEAN_COLOR.link}
                                    docId={n.docId}
                                    docTypes={s.docTypes}
                                    onClick={() =>
                                      callAction(
                                        'openDocInCurrentMode',
                                        n.docId,
                                        (n.label ? `${n.label} — ` : '') + title,
                                        n.id,
                                      )
                                    }
                                  />
                                  {s.docAiGenerated[n.docId] ? (
                                    <span className="aidoc-claude-badge" title="Generated by Claude">
                                      ✦ Claude
                                    </span>
                                  ) : null}
                                </>
                              ) : (
                                <span style={{ color: 'var(--ink-faint)' }}>—</span>
                              )}
                              {/* The way back: this node's rows in the Documents tab. A link, not
                                  an .aidoc-actbtn — those are the console's ACTIONS, and the suite
                                  counts them. */}
                              <button
                                type="button"
                                className="logs-link aidoc-wf"
                                data-node={n.id}
                                title="Where this node's documents stand — the Documents tab, narrowed to them"
                                onClick={() => onShowInDocuments(n.id)}
                              >
                                See WF state
                              </button>
                            </div>
                          </td>
                          <td>
                            <LangCell node={n} locales={locales} />
                          </td>
                          {actions.length ? (
                            <td>
                              <ActionButtons ids={[n.id]} />
                            </td>
                          ) : null}
                          <td className="rowstate-cell">
                            {state ? <span className={`rowstate ${state.cls}`}>{state.text}</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div id="dash-ai-foot">
        <button className="pbtn" id="aidoc-cancel" onClick={onLeave}>
          Cancel
        </button>
        <button
          className="pbtn primary"
          id="aidoc-generate"
          disabled={generating}
          onClick={() => generate([...selected])}
        >
          Generate Doc
        </button>
        <span id="aidoc-progress">{progress}</span>
      </div>
    </div>
  );
}
